import { faWalking, faCarSide, faBusAlt, faTrain, faBicycle, faTaxi, faShip, faPlaneDeparture, faRoute, faSave, faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import { useEffect } from "react";
import { PlannerMapPlace, PlannerLineData, getLat, getLng } from "src/types/interfaces";
import { Button } from '../Button/Button';

interface PlannerTimelineProps {
  isVisible: boolean;
  itineraryItems: PlannerMapPlace[];
  connectingLines: PlannerLineData[];
  activeItemId: string | null;
  onClose: () => void;
  onExport: () => void;
  onSavePlan?: () => void;
  onItemClick: (item: PlannerMapPlace) => void;
  timelineScrollAreaRef: React.RefObject<HTMLDivElement>;
}

const getTransportIcon = (transportType?: string) => {
  const type = (transportType || '').toLowerCase();
  if (type.includes('walk')) return faWalking;
  if (type.includes('car') || type.includes('driv')) return faCarSide;
  if (type.includes('bus') || type.includes('transit') || type.includes('public')) return faBusAlt;
  if (type.includes('train') || type.includes('subway') || type.includes('metro')) return faTrain;
  if (type.includes('bike') || type.includes('cycl')) return faBicycle;
  if (type.includes('taxi') || type.includes('cab')) return faTaxi;
  if (type.includes('boat') || type.includes('ferry')) return faShip;
  if (type.includes('plane') || type.includes('fly')) return faPlaneDeparture;
  return faRoute;
};

export const PlannerTimeline: React.FC<PlannerTimelineProps> = ({
  isVisible,
  itineraryItems,
  connectingLines,
  activeItemId,
  onClose,
  onExport,
  onSavePlan,
  onItemClick,
  timelineScrollAreaRef,
}) => {
  useEffect(() => {
    if (isVisible && activeItemId && timelineScrollAreaRef.current) {
      const activeElement = timelineScrollAreaRef.current.querySelector(`[data-timeline-id="${activeItemId}"]`);
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [isVisible, activeItemId, itineraryItems, timelineScrollAreaRef]);

  const renderTimelineEntries = () => {
    const entries: JSX.Element[] = [];
    itineraryItems.forEach((item, index) => {
      const isItemActive = item.placeId === activeItemId;
      entries.push(
        <Box
          key={`item-${item.placeId}`}
          className={`timeline-entry location-entry ${isItemActive ? 'active' : ''}`}
          onClick={() => onItemClick(item)}
          data-timeline-id={item.placeId}
        >
          <Typography variant="subtitle2" className="timeline-time">
            {item.time || 'Flexible'}
          </Typography>
          <Box className="timeline-entry-connector">
            <Box className={`timeline-entry-dot location-dot ${isItemActive ? 'active' : ''}`} />
            {index < itineraryItems.length - 1 && <Box className="timeline-entry-line" />}
          </Box>
          <Paper elevation={isItemActive ? 3 : 1} className="timeline-entry-content">
            <Typography variant="subtitle1" className="timeline-entry-title">{item.name}</Typography>
            <Typography variant="body2" className="timeline-entry-description">{item.description}</Typography>
            {item.duration && (
              <Typography variant="caption" className="timeline-entry-duration">{item.duration}</Typography>
            )}
          </Paper>
        </Box>
      );

      if (index < itineraryItems.length - 1) {
        const nextItem = itineraryItems[index + 1];
        const line = connectingLines.find(l =>
          (l.start_location_id === item.placeId && l.end_location_id === nextItem.placeId) ||
          (l.start && l.end &&
            l.start.lat === getLat(item) && l.start.lng === getLng(item) &&
            l.end.lat === getLat(nextItem) && l.end.lng === getLng(nextItem))
        );

        if (line) {
          entries.push(
            <Box key={`line-${line.id}`} className="timeline-entry transport-entry">
              <Typography variant="subtitle2" className="timeline-time" />
              <Box className="timeline-entry-connector">
                <Box className="timeline-entry-dot transport-dot" />
                {(index < itineraryItems.length - 2 || (index === itineraryItems.length - 2 && connectingLines.some(l => l.start_location_id === nextItem.placeId || (l.start && l.start.lat === getLat(nextItem) && l.start.lng === getLng(nextItem))))) &&
                  <Box className="timeline-entry-line" />
                }
              </Box>
              <Box className="timeline-entry-content transport-content">
                <Typography variant="subtitle2" className="timeline-entry-title">
                  <FontAwesomeIcon icon={getTransportIcon(line.transport)} style={{ marginRight: '8px' }} />
                  {line.transport || 'Travel'}
                </Typography>
                <Typography variant="body2" className="timeline-entry-description" title={line.name}>{line.name}</Typography>
                {line.travelTime && (
                  <Typography variant="caption" className="timeline-entry-duration">{line.travelTime}</Typography>
                )}
              </Box>
            </Box>
          );
        }
      }
    });
    return entries;
  };

  return (
    <Paper className={`timeline-panel ${isVisible ? 'visible' : ''}`}>
      <Box className="timeline-header-bar">
        <Typography variant="h6">Your Day Plan</Typography>
        <Box>
          {onSavePlan ? (
            <Button startIcon={<FontAwesomeIcon icon={faSave} />} onClick={onSavePlan} intent="primary" size="small">
              Save Plan
            </Button>
          ) : (
            <Button startIcon={<FontAwesomeIcon icon={faDownload} />} onClick={onExport} intent="secondary" size="small">
              Export
            </Button>
          )}
          <IconButton onClick={onClose} sx={{ color: '#555' }}>
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </Box>
      </Box>
      <Box className="timeline-items-area" ref={timelineScrollAreaRef}>
        {itineraryItems.length > 0 ? renderTimelineEntries() : <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No items in your plan yet.</Typography>}
      </Box>
    </Paper>
  );
};
